import React, {forwardRef, useCallback, useEffect, useRef, useState} from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import {useNavigate, useParams} from 'react-router-dom'
import {getListCustomShapeTemplate} from '../../../../services/ArtworkTemplateServices'
import {toaster} from '../../../../shared/PFToast'
import {useSelectedVariantContext} from '../../context/SelectedVariantContext'
import {SHAPE_TYPES, FILTER_VALUE} from '../../../../constant/shapes'
import {ShapeItem} from './ShapeItem'
import {NoData} from './NoData'
import LoadingSpinner from './LoadingSpinner'
import {CancelToken, Cancel} from 'axios'
import uniqBy from 'lodash/uniqBy'
import {useThrottle} from '../../../../hooks/useThrottle'
import {BASIC_SHAPES, CUTOUT_SHAPES} from './constants'
import {useDraftShapes} from '../../../custom-shape/ui/hooks/useDraftShapes'
import ConditionalRender from '../../../../shared/ConditionalRender'
import {hasFeatureTag} from '../../../../helpers/settings'

const CUSTOM_SHAPE_EDIT_PAYLOAD_KEY = 'customShapeEditPayload'

/**
 * List of shapes component
 * @param {Object} props - Component props
 * @param {string} props.typeFilter - Filter for shapes ('all', 'basic', 'custom')
 * @param {string} props.nameFilter - Filter for shapes name
 * @param {string} [props.productId] - Product id for callback URL when navigating to edit
 * @returns {JSX.Element} - List of shapes component
 */
export const ModalShapesContent = forwardRef(({typeFilter, nameFilter, productId: productIdProp}, ref) => {
    const {
        state: {triggerLoadTemplate},
    } = useSelectedVariantContext()
    const navigate = useNavigate()
    const {productId: productIdFromParams} = useParams()
    const productId = productIdProp ?? productIdFromParams
    const cancelSourceRef = useRef(null)

    const [hasMore, setHasMore] = useState(true)
    const [loading, setLoading] = useState(false)
    const [params, setParams] = useState({
        page: 1,
        limit: 15,
    })
    const [listTemplateShapes, setListTemplateShapes] = useState([])
    const {drafts, loading: draftsLoading} = useDraftShapes()

    const fetchTemplateShapes = async ({page, name}, isFetchMore) => {
        try {
            setLoading(true)
            cancelSourceRef.current = CancelToken.source()

            const {data} = await getListCustomShapeTemplate(
                {
                    limit: params.limit,
                    page,
                    name,
                },
                {cancelToken: cancelSourceRef.current.token},
            ).then((res) => {
                if (!res.success) {
                    throw new Error(res.message)
                }
                return res
            })

            const mapped = data.designTemplate
                .filter(Boolean)
                .map((item) => ({type: SHAPE_TYPES.TEMPLATE, template: item}))

            setHasMore(page < Math.ceil(data.total / data.limit))
            setParams((prev) => ({...prev, page}))
            setListTemplateShapes((prev) => uniqBy(isFetchMore ? [...prev, ...mapped] : mapped, 'template._id'))
        } catch (error) {
            if (error instanceof Cancel) {
                return console.error(error)
            }
            toaster({
                type: 'error',
                message: error.message,
            })
        } finally {
            setLoading(false)
        }
    }

    const throttledFetchMore = useThrottle(
        () => {
            if (loading || !hasMore) {
                return
            }
            fetchTemplateShapes({page: params.page + 1, name: nameFilter}, true)
        },
        400,
        {
            leading: true,
            trailing: false,
        },
    )

    const customShapeStep2Enabled = hasFeatureTag('custom_shape_step2')

    useEffect(() => {
        if (!customShapeStep2Enabled) return
        if (cancelSourceRef.current) {
            /*
             * Cancel previous request
             * - [v] Filter name change while fetching
             * - [x] Fetch more while fetching - early checked
             */
            cancelSourceRef.current.cancel('CANCEL_PREV_REQUEST')
        }
        setHasMore(true)
        fetchTemplateShapes({page: 1, name: nameFilter}, false)
    }, [nameFilter, triggerLoadTemplate, customShapeStep2Enabled])

    useEffect(() => {
        const container = document.getElementById('ModalShapesContent')
        if (container) {
            container.scrollTo({top: 0})
        }
    }, [typeFilter])

    useEffect(() => {
        /**
         * Perform cleanup when component unmount
         */
        return () => {
            if (cancelSourceRef.current) {
                cancelSourceRef.current.cancel('CANCEL_UNMOUNT')
            }
        }
    }, [])
    const listBasicShape = BASIC_SHAPES.concat(CUTOUT_SHAPES).filter((item) =>
        item?.name?.toLowerCase().includes(nameFilter?.toLowerCase()),
    )
    const isDisplayBasic = [FILTER_VALUE.BASIC, FILTER_VALUE.ALL].includes(typeFilter)
    const isDisplayTemplate = [FILTER_VALUE.TEMPLATE, FILTER_VALUE.ALL].includes(typeFilter)
    const isNoBasicShape = !isDisplayBasic || listBasicShape.length === 0
    const isNoTemplateShape = !isDisplayTemplate || (!loading && listTemplateShapes.length === 0)
    const isNoDraftShape = !isDisplayTemplate || (!draftsLoading && drafts.length === 0)

    const handleEditTemplate = useCallback(
        (template) => {
            const payload = {
                source: 'template',
                templateId: template._id,
                doc: template.data,
                meta: {
                    id: template._id,
                    name: template.name,
                    description: template.description,
                },
            }
            try {
                window.localStorage.setItem(CUSTOM_SHAPE_EDIT_PAYLOAD_KEY, JSON.stringify(payload))
            } catch {
                toaster({type: 'error', message: 'Failed to prepare edit'})
                return
            }
            const callbackPath = productId ? `/${productId}` : ''
            navigate(`/create-custom-shape?callbackUrl=${callbackPath}&edit=1`)
        },
        [navigate, productId],
    )

    const handleEditDraft = useCallback(
        (draft) => {
            const payload = {source: 'draft', draftId: draft.id}
            try {
                window.localStorage.setItem(CUSTOM_SHAPE_EDIT_PAYLOAD_KEY, JSON.stringify(payload))
            } catch {
                toaster({type: 'error', message: 'Failed to prepare edit'})
                return
            }
            const callbackPath = productId ? `/${productId}` : ''
            navigate(`/create-custom-shape?callbackUrl=${callbackPath}&edit=1`)
        },
        [navigate, productId],
    )

    return (
        <div className="ModalShapesContent" id="ModalShapesContent" ref={ref}>
            <InfiniteScroll
                dataLength={listTemplateShapes.length}
                next={throttledFetchMore}
                hasMore={hasMore}
                scrollableTarget="ModalShapesContent"
                className="InfiniteScrollContainer"
            >
                <ConditionalRender condition={customShapeStep2Enabled} fallback={null}>
                    <>
                        {/* Draft */}
                        <div
                            className="GroupOfTemplateShapes GroupOfDraftShapes"
                            style={{
                                display: isDisplayTemplate ? 'block' : 'none',
                            }}
                        >
                            <h4 className="GroupOfShapesTitle">Draft</h4>
                            <div className="ListOfShapes GroupOfTemplateShapes">
                                {drafts
                                    .filter((d) => d.previewUrl)
                                    .map((draft) => (
                                        <ShapeItem
                                            key={draft.id}
                                            shapeType={SHAPE_TYPES.TEMPLATE}
                                            template={{
                                                _id: draft.id,
                                                preview_image: draft.previewUrl,
                                                name: 'Draft',
                                                description: 'Draft',
                                            }}
                                            canvasSize={100}
                                            name="Draft"
                                            description="Draft"
                                            onEdit={() => handleEditDraft(draft)}
                                        />
                                    ))}
                                {isNoDraftShape && <NoData />}
                                {draftsLoading && <LoadingSpinner />}
                            </div>
                        </div>
                        {/* Shape template */}
                        <div
                            className="GroupOfShapes GroupOfTemplateShapes"
                            style={{
                                display: isDisplayTemplate ? 'block' : 'none',
                            }}
                        >
                            <h4 className="GroupOfShapesTitle">Your shapes</h4>
                            <div className="ListOfShapes">
                                {listTemplateShapes.map(({type, template}) => (
                                    <ShapeItem
                                        key={template._id}
                                        shapeType={type}
                                        template={template}
                                        canvasSize={100}
                                        name={template.name}
                                        description={template.description || template.name}
                                        onEdit={() => handleEditTemplate(template)}
                                    />
                                ))}
                                {/* No data */}
                                {isNoTemplateShape && <NoData />}
                                {/* Loading */}
                                {loading && <LoadingSpinner />}
                            </div>
                        </div>
                    </>
                </ConditionalRender>
                {/* Basic shape */}
                <div
                    className="GroupOfShapes GroupOfBasicShapes"
                    style={{
                        display: isDisplayBasic ? 'block' : 'none',
                    }}
                >
                    <h4 className="GroupOfShapesTitle">Basic shapes</h4>
                    <div className="ListOfShapes">
                        {listBasicShape.map(({type, name, description}, idx) => (
                            <ShapeItem
                                key={type + idx}
                                shapeType={type}
                                // name={name}
                                description={description || name}
                            />
                        ))}
                        {/* No data */}
                        {isNoBasicShape && <NoData />}
                    </div>
                </div>
            </InfiniteScroll>
        </div>
    )
})

export default ModalShapesContent

